export const ADMIN_ROLES = [
  "support_readonly",
  "operations_readonly",
  "security_readonly",
  "operations_admin",
] as const;

export const ADMIN_PERMISSIONS = [
  "admin.overview.read",
  "admin.failed_flows.read",
  "admin.device_kpis.read",
  "admin.users.read",
  "admin.user_data.read",
  "admin.jobs.read",
  "admin.releases.read",
  "admin.security_summary.read",
  "admin.recognition_audits.read",
  "admin.recognition_audits.raw_access",
  "admin.recognition_audits.export_candidate.read",
  "admin.phase2_flags.read",
  "admin.phase2_flags.write",
  "admin.case_reviews.read",
  "admin.case_reviews.write",
  "admin.tag_similarity.read",
  "admin.tag_similarity.write",
  "admin.community_reports.read",
  "admin.community_reports.write",
  "admin.health_content.read",
  "admin.health_content.write",
  "admin.conversations.metadata.read",
  "admin.conversations.content.read",
] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];
export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number];

const ROLE_PERMISSION_MAP: Record<AdminRole, readonly AdminPermission[]> = {
  support_readonly: [
    "admin.overview.read",
    "admin.failed_flows.read",
    "admin.device_kpis.read",
    "admin.users.read",
    "admin.user_data.read",
    "admin.jobs.read",
    "admin.recognition_audits.read",
    "admin.recognition_audits.raw_access",
    "admin.conversations.metadata.read",
  ],
  operations_readonly: [
    "admin.overview.read",
    "admin.failed_flows.read",
    "admin.device_kpis.read",
    "admin.jobs.read",
    "admin.releases.read",
    "admin.recognition_audits.read",
    "admin.recognition_audits.raw_access",
    "admin.conversations.metadata.read",
  ],
  security_readonly: [
    "admin.overview.read",
    "admin.failed_flows.read",
    "admin.device_kpis.read",
    "admin.jobs.read",
    "admin.releases.read",
    "admin.security_summary.read",
    "admin.recognition_audits.read",
    "admin.recognition_audits.raw_access",
    "admin.recognition_audits.export_candidate.read",
    "admin.phase2_flags.read",
    "admin.case_reviews.read",
    "admin.tag_similarity.read",
    "admin.community_reports.read",
    "admin.health_content.read",
    "admin.conversations.metadata.read",
    "admin.conversations.content.read",
  ],
  operations_admin: [
    "admin.overview.read",
    "admin.failed_flows.read",
    "admin.device_kpis.read",
    "admin.jobs.read",
    "admin.releases.read",
    "admin.recognition_audits.read",
    "admin.recognition_audits.raw_access",
    "admin.phase2_flags.read",
    "admin.phase2_flags.write",
    "admin.case_reviews.read",
    "admin.case_reviews.write",
    "admin.tag_similarity.read",
    "admin.tag_similarity.write",
    "admin.community_reports.read",
    "admin.community_reports.write",
    "admin.health_content.read",
    "admin.health_content.write",
    "admin.conversations.metadata.read",
  ],
};

export function getAdminPermissionsForRoles(roles: AdminRole[]): AdminPermission[] {
  const permissions = new Set<AdminPermission>();

  for (const role of roles) {
    const mappedPermissions = ROLE_PERMISSION_MAP[role];

    for (const permission of mappedPermissions) {
      permissions.add(permission);
    }
  }

  return ADMIN_PERMISSIONS.filter((permission) => permissions.has(permission));
}

export function hasAdminPermission(
  permissions: AdminPermission[],
  permission: AdminPermission,
): boolean {
  return permissions.includes(permission);
}
